import strings from 'l10n'
import { overAdvanceTypeLabel } from 'constants/overAdvance'
import { empty, getFactorSettingsFromLocalStorage, isDefined, stringToBoolean } from './common'
import colors from 'theme/colors'
import { billingDeliveryDropdownOptions, currencyDropDownOptions } from 'constants/options'
import { InvoiceChangelogInfo, InvoiceSummaryInfo, isInvoiceStatus } from 'types/invoice'
import { TermSummaryInfo } from 'types/terms'
import { UserInfo } from 'types/users'
import { FieldValues } from 'react-hook-form'
import { FundingInstructionsCreateRequest, FundingInstructionsUpdateRequest } from 'types/fundingInstructions'
import { DropDownOptions } from 'types/common'
import { BrokerInfo, CompanyClientAdminSettingsResponse, CompanyEmployeeEnum } from 'types/company'
import uuid from 'react-uuid'
import { OutOfSystemInvoicesInfo } from 'types/payments'
import { FuelAdvanceInfo, FuelAdvanceType } from 'types/fuelAdvances'
import { ReserveReleaseInfo } from 'types/reserveRelease'
import { DraftPaymentStatus } from 'types/paymentBatches'
import { getCreatableValue } from 'components/hookForm/CreatableFormField'
import { GridRenderCellParams } from '@mui/x-data-grid'
import { PartnerType } from 'types/commissionPartners'
import dayjs from 'dayjs'
import { CustomGlCodeInfo } from 'types/customGlCodes'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { OverAdvanceType } from 'types/overAdvance'
import { ledgerCurrencyDisplay, toIsoCurrencyCode } from 'constants/currencies'
import { loadActiveCurrencyFilterValueForDisplay } from 'context/CurrencyFilter/currencyFilterStorage'

dayjs.extend(utc)
dayjs.extend(timezone)

export function addAllValues(obj: Record<string, number>): number {
  const valuesArray = Object.values(obj)
  const sum = valuesArray.reduce((total, currentValue) => total + currentValue, 0)
  return sum
}

export function def(value?: string | number | null, def = '-') {
  return value ?? def
}

export const formattedTotalRate = (totalRate: string = '') => {
  return Number(removeCommas(totalRate.replace('$', '')))
}

export function percentage(value: number = 0, maximumFractionDigits: number = 4): string {
  // Ensure that maximumFractionDigits is a finite number and at least 2 to avoid issues with Intl.NumberFormat
  const safeMaximumFractionDigits = Number.isFinite(maximumFractionDigits)
    ? Math.max(2, Math.floor(maximumFractionDigits))
    : 4

  const f = new Intl.NumberFormat(navigatorLocale(), {
    maximumFractionDigits: safeMaximumFractionDigits,
    minimumFractionDigits: 2,
  })
  return f.format(value * 100)
}

export const removeCommas = (value: string | number) => {
  if (typeof value === 'number') {
    return value
  }
  return value.replace(/,/g, '.')
}

export const removeSpecialCharactersFromTheMiddleOfAString = (value: string) => {
  if (isDefined(value) && value !== '') return value.replace(/(?!^)[^\d.]/g, '')
  return ''
}

export function toDecimal(value?: string | number) {
  if (isDefined(value)) {
    const finalValue: number = typeof value == 'string' ? Number(value.replace(/,/g, '.')) : value
    return (finalValue / 100).toFixed(4)
  }
  return null
}

export function toDecPercent(value?: string | number) {
  if (isDefined(value)) {
    const finalValue: number = typeof value == 'string' ? Number(value.replace(/,/g, '.')) : value
    return (finalValue / 100).toFixed(6)
  }
  return null
}

type DateTimeFormatArgs = {
  date?: string | null
  locale?: string
  defValue?: string
  formatOptions?: Intl.DateTimeFormatOptions
}

//Only for displaying, not for parsing to input components
export function dateTimeFormat(args: DateTimeFormatArgs) {
  const { date, locale = navigatorLocale(), defValue = '-', formatOptions = {} } = args
  const d = date ? new Date(date) : null
  if (d === null) return defValue
  const formatter = new Intl.DateTimeFormat(locale, formatOptions)
  return formatter.format(d)
}

export function utcDateFormat(date: string | null) {
  return dateTimeFormat({
    date: date,
    formatOptions: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    },
  })
}

/**
 * Returns the current date in the format yyyy-MM-dd
 * @param timeZone
 * @returns Current date string in format yyyy-MM-dd
 */
export function now(timeZone?: string | undefined) {
  const date = new Date()
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timeZone,
  }
  const formatter = new Intl.DateTimeFormat('en-CA', options)
  const formattedDate = formatter.format(date)
  return formattedDate
}

export function currentLocaleDateTime(timeZone?: string | undefined) {
  const formatter = new Intl.DateTimeFormat(navigatorLocale(), {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: timeZone,
  })
  return formatter.format(new Date())
}

export function localeDateTimeFormat(date?: string | null, timeZone?: string | undefined) {
  return dateTimeFormat({
    date: date,
    formatOptions: {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: timeZone,
    },
  })
}

export function timezoneDateTimeFormat(
  date?: string | Date | null,
  timeZone?: string | undefined,
  format: string = 'MM/DD/YYYY h:mm A'
) {
  if (!date) return '-'

  const parsedDate = timeZone ? dayjs.utc(date).tz(timeZone) : dayjs(date)
  if (!parsedDate.isValid()) return '-'

  return parsedDate.format(format)
}

export function endOfDayInTimezone(timeZone?: string | undefined) {
  return (timeZone ? dayjs().tz(timeZone) : dayjs()).endOf('day').toDate()
}

export function isSameDayInTimezone(
  date?: string | Date | null,
  compareDate?: string | Date | null,
  timeZone?: string
) {
  if (!date || !compareDate) return false

  const leftDate = timeZone ? dayjs.utc(date).tz(timeZone) : dayjs(date)
  const rightDate = timeZone ? dayjs.utc(compareDate).tz(timeZone) : dayjs(compareDate)

  if (!leftDate.isValid() || !rightDate.isValid()) return false

  return leftDate.isSame(rightDate, 'day')
}

export function forceDisplayZero(value: number) {
  if (value === null || value === undefined) return null
  if (value === 0) return '0'
  return value
}

export type MoneyMode = 'iso' | 'plain'

export type FormatMoneyOptions = {
  minFrac?: number
  showSymbol?: boolean
  mode?: MoneyMode
  locale?: string
}

export function formatMoney(
  amount?: number | string | null,
  currency: string = 'USD',
  { minFrac = 2, showSymbol = true, mode = 'plain', locale }: FormatMoneyOptions = {}
): string {
  if (amount === null || amount === undefined) return '-'
  const value = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(value)) return '-'

  const number = new Intl.NumberFormat(locale, { style: 'decimal', minimumFractionDigits: minFrac }).format(value)

  const iso = (toIsoCurrencyCode(currency) ?? 'USD').toUpperCase()
  if (mode === 'iso') {
    const display = ledgerCurrencyDisplay[iso]
    const prefix = display ? (display.abbr ? display.abbr + (showSymbol ? display.symbol : ' ') : display.symbol) : ''
    return prefix + number
  }

  if (!showSymbol) return number
  return '$' + number
}

export function resolveMoneyMode(hasActiveCurrency: boolean): MoneyMode {
  return hasActiveCurrency ? 'iso' : 'plain'
}

export function currencyFormat(
  amount?: number | string | null,
  currency: string = 'USD',
  minimumFractionDigits: number = 2,
  showCurrencySymbol: boolean = true
) {
  const factorSettings = getFactorSettingsFromLocalStorage()
  const enableCurrencySymbol = stringToBoolean(factorSettings?.enableCurrencySymbol)
  const enableCurrencyFilter = stringToBoolean(factorSettings?.enableCurrencyFilter)
  const activeFilter = enableCurrencyFilter ? loadActiveCurrencyFilterValueForDisplay() : undefined

  const resolvedCurrency = (activeFilter ?? currency ?? '').trim()
  const mode: MoneyMode = resolveMoneyMode(Boolean(activeFilter))

  return formatMoney(amount, resolvedCurrency || 'USD', {
    mode,
    minFrac: minimumFractionDigits,
    showSymbol: enableCurrencySymbol && showCurrencySymbol && resolvedCurrency.length > 0,
    locale: navigatorLocale(),
  })
}

export function dollarFormat(amount: number) {
  return currencyFormat(amount)
}

export function percentageFormat(value?: number | null) {
  if (isDefined(value)) {
    return percentage(value) + '%'
  }
  return '-'
}

export function percentageFormatWithMaxDigit(value?: number | null, maximumDigits?: number) {
  if (isDefined(value)) {
    return percentage(value, maximumDigits) + '%'
  }
  return '-'
}

export function megaByteFormat(bytes?: number | null) {
  if (isDefined(bytes)) {
    const megaBytes = bytes / 1_048_576
    const f = new Intl.NumberFormat(navigatorLocale(), {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return f.format(megaBytes) + 'MB'
  }
  return '-'
}

export function boolFormat(val?: boolean | null) {
  if (val === true) return strings.YES
  if (val === false) return strings.NO
  return '-'
}

function navigatorLocale() {
  const locale = navigator.language ?? 'en-US'
  return locale
}

export function setEndOfDay(date: string): Date {
  const dateNoTime = date.split('T')[0]
  const finalDateTime = `${dateNoTime}T23:59:59.999Z`
  const finalDate = new Date(finalDateTime)

  return finalDate
}

export function setEndOfDayString(date: string): string {
  const dateNoTime = date.split('T')[0]
  const finalDateTime = `${dateNoTime}T23:59:59.999Z`
  return finalDateTime
}

export function setTimeToZero(date: string): Date {
  const dateNoTime = date.split('T')[0]
  const finalDate = new Date(`${dateNoTime}T00:00:00.000Z`)
  return finalDate
}

export function findCurrencyOption(currencyAbbreviators: string | undefined) {
  return currencyDropDownOptions.find((option) => option.value === currencyAbbreviators)?.label
}

export const formattedDocumentType = (documentType: string, otherDocumentType?: string) => {
  switch (documentType) {
    case 'INVOICE':
      return 'Invoice'
    case 'PAYMENT':
      return 'Payment'
    case 'DISBURSEMENT':
      return 'Disbursement'
    case 'NOA':
      return 'Notice of Assignment'
    case 'POI':
      return 'Proof of Insurance'
    case 'UCC':
      return 'Uniform Commercial Code (UCC)'
    case 'CONTRACT':
      return 'Contract'
    case 'TAX_DOC':
      return 'Tax Doc'
    case 'LETTER_OF_RELEASE':
      return 'Letter of Release'
    case 'BUY_OUT_DETAILS':
      return 'Buy out Details'
    case 'DEBTOR_RELEASE':
      return 'Debtor Release'
    case 'CLIENT_TERMINATION_RELEASE':
      return 'Client termination Release'
    case 'LOAN_DOCS':
      return 'Loan Docs'
    case 'LEGAL_DOCS':
      return 'Legal Docs'
    case 'IRS_DOCS':
      return 'IRS Docs'
    case 'PO_DOCS':
      return 'PO Docs'
    case 'PERSONAL_CLIENT_INFO':
      return 'Personal client info'
    case 'COLLECTIONS_PAYMENT_ISSUES':
      return 'Collections & Payment Issues'
    case 'CREDIT_SAFE_REPORT':
      return 'Credit Safe Report'
    case 'INVOICE_NOA':
      return 'NOA Invoice'
    case 'FUEL_ADVANCE':
      return 'Fuel Advance'
    case 'OUT_OF_SYSTEM_INVOICE':
      return 'Out of System Invoice'
    case 'REMITTANCE':
      return 'Remittance'
    case 'IMPORT_CSV':
      return 'Import CSV'
    case 'REPORT_EXPORT':
      return 'Report Export'
    case 'ANSONIA':
      return 'Ansonia'
    case 'LEGAL_DISCLOSURE':
      return 'Legal Disclosure'
    case 'MONTHLY_STATEMENT':
      return 'Monthly Statement'
    case 'OVER_ADVANCE':
      return 'Over Advance'
    case 'NOA_RELATION':
      return 'NOA (Relation)'
    case 'INVOICE_NON_BILLING':
      return 'Non Billing'
    case 'OTHER':
      if (otherDocumentType) {
        return formattedOtherDocumentType(otherDocumentType)
      }
      return 'Other'
    default:
      return 'Other'
  }
}

export const formattedBillingClassification = (documentType: string) => {
  switch (documentType) {
    case 'INVOICE':
      return strings.INVOICE_BILLING
    case 'INVOICE_NON_BILLING':
      return strings.INVOICE_NON_BILLING
    default:
      return '-'
  }
}

export const formatterVerificationStatusInvoice = (verificationStatus: string) => {
  switch (verificationStatus) {
    case 'DENIED':
      return strings.VERIFY_STATUS_DENIED
    case 'NOT_VERIFIED':
      return strings.VERIFY_STATUS_NOT_VERIFIED
    case 'PENDING':
      return strings.VERIFY_STATUS_PENDING
    case 'VERIFIED':
      return strings.VERIFY_STATUS_VERIFIED
    default:
      return '-'
  }
}

export const formattedOtherDocumentType = (otherDocumentType: string) => {
  const otherDocumentTypeTrimmed = otherDocumentType.trim()
  const string = otherDocumentTypeTrimmed.toLocaleLowerCase()
  const words = string.split(' ')
  const filteredWords = words.filter((word) => word !== '')
  const capitalizedWords = filteredWords.map((word) => {
    return word[0].toUpperCase() + word.slice(1)
  })
  return capitalizedWords.join(' ')
}

export const formattedInvoiceGroupsStatus = (status?: string) => {
  switch (status) {
    case 'NOT_FUNDED':
      return strings.NOT_FUNDED.SINGLE
    case 'FUNDED':
      return strings.FUNDED.SINGLE
    default:
      return ''
  }
}

export const formattedPaymentChannel = (paymentChannel?: string) => {
  switch (paymentChannel) {
    case 'FACTOR_CLOUD':
      return strings.FILTER_FACTOR_CLOUD
    case 'TANK':
      return strings.FILTER_TANK
    case 'ATOB':
      return strings.FILTER_ATOB
    case 'BANKSHOT':
      return strings.FILTER_BANKSHOT
    case 'TEXAS_CAPITAL':
      return strings.FILTER_TEXAS_CAPITAL
    default:
      return '-'
  }
}

export const formatChangelog = (log: InvoiceChangelogInfo) => {
  let output = log.userEmail || log.apiKeyName || ''
  if (log.logType === 'VERIFICATION') {
    output = `${formatVerificationInvoiceStatus(log.status)} by ${output}`
  } else if (log.logType === 'COLLECTION') {
    output = `${strings.COLLECTION}: ${output}`
  } else if (log.logType === 'UPDATE') {
    output = `${strings.INVOICE} ${strings.UPDATE}: ${output}`
  }
  return output
}

export const formatChangelogInvoiceStatus = (log: InvoiceChangelogInfo) => {
  let status = log.status
  if (!isInvoiceStatus(status)) return ''
  return `${strings.STATUS.LABEL} : ${formattedStatus(status)}`
}

export const formatChangelogInvoiceMethod = (log: InvoiceChangelogInfo) => {
  let method = log.method
  return `${strings.METHOD} : ${formatVerificationMethodInvoiceStatus(method)}`
}

export const formatVerificationInvoiceStatus = (type: string | undefined) => {
  if (type === 'VERIFIED') return strings.VERIFIED
  if (type === 'DENIED') return strings.DENIED
  if (type === 'PENDING') return strings.PENDING.SINGLE
  if (type === 'NOT_VERIFIED') return strings.NOT_VERIFIED
  if (type === '') return '-'

  return type
}

export const formatVerificationMethodInvoiceStatus = (type: string) => {
  return capitalizeWords(type)
}

export function formatInvoiceDate(invoiceDate: string) {
  const currentDate = new Date().toISOString().split('T')[1]
  return invoiceDate.concat('T').concat(currentDate)
}

export function isUpdatedInvoiceDate(invoiceDate: string, dataInvoiceDate: string) {
  return invoiceDate.split('T')[0] === dataInvoiceDate ? invoiceDate : formatInvoiceDate(dataInvoiceDate)
}

export const formatPaymentMethod = (type?: string | null) => {
  const paymentMethod = type ?? ''
  if (empty(paymentMethod)) return '-'
  return paymentMethod.replace(/_/g, ' ').toUpperCase()
}

export const formattedStatus = (type: string) => {
  //if (!isInvoiceStatus(type)) return ''

  if (type === 'PENDING') return strings.PENDING.SINGLE
  if (type === 'APPROVED_FOR_FUNDING') return strings.APPROVED_FOR_FUNDING.SINGLE
  if (type === 'FUNDED') return strings.FUNDED.SINGLE
  if (type === 'PAID') return strings.CLOSED
  if (type === 'REJECTED') return strings.REJECTED
  if (type === 'DELETED') return strings.DELETED
  if (type === 'APPROVED') return strings.APPROVED
  if (type === 'OPEN') return strings.OPEN
  if (type === 'CLOSED') return strings.CLOSED
  if (type === 'RETURNING') return strings.RETURNING

  return type
}

export const formattedFuelAdvanceStatus = (type: string) => {
  if (type === 'PENDING') return strings.PENDING.SINGLE
  if (type === 'APPROVED') return strings.APPROVED
  if (type === 'OPEN') return strings.OPEN
  if (type === 'REJECTED') return strings.REJECTED
  if (type === 'CLOSED') return strings.CLOSED

  return type
}

export const formattedCompanyType = (type: string) => {
  if (type === 'Client') return strings.CLIENTS
  if (type === 'Debtor') return strings.DEBTORS

  return type
}

export const formattedInformation = (listData?: InvoiceSummaryInfo[] | FuelAdvanceInfo[] | ReserveReleaseInfo[]) => {
  const modifiedData = listData?.map((data) => {
    return {
      ...data,
      status: formattedStatus(data.status),
    }
  })
  return modifiedData
}

export const formatValueMethodFee = (invoicePreviewResponse: any, watch: string) => {
  if (invoicePreviewResponse && watch) {
    const selectedOption = invoicePreviewResponse?.fundingInstruction.find((item: any) => item.id === watch)
    if (selectedOption?.paymentMethod === 'ACH') {
      return { nameFee: strings.ACH_FEE, valueFee: invoicePreviewResponse?.paymentFees.achFee || 0 }
    } else if (selectedOption?.paymentMethod === 'WIRE') {
      return { nameFee: strings.WIRE_FEE, valueFee: invoicePreviewResponse?.paymentFees.wireFee || 0 }
    } else if (selectedOption?.paymentMethod === 'CHECK') {
      return { nameFee: strings.CHECK_FEE, valueFee: invoicePreviewResponse?.paymentFees.checkFee || 0 }
    } else if (selectedOption?.paymentMethod === 'FUEL_CARD') {
      return {
        nameFee: strings.FUEL_CARD_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.fuelCardFee || 0,
      }
    } else if (selectedOption?.paymentMethod === 'SAME_DAY_ACH') {
      return {
        nameFee: strings.SAME_DAY_ACH_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.sameDayAchFee || 0,
      }
    } else if (selectedOption?.paymentMethod === 'MONEY_CODE') {
      return {
        nameFee: strings.MONEY_CODE_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.moneyCodeFee || 0,
      }
    } else if (selectedOption?.paymentMethod === 'DIGITAL_WALLET') {
      return {
        nameFee: strings.DIGITAL_WALLET_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.digitalWalletFee || 0,
      }
    } else if (selectedOption?.paymentMethod === 'INSTANT') {
      return {
        nameFee: strings.INSTANT_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.instantFee || 0,
      }
    } else if (selectedOption?.paymentMethod === 'RTP') {
      return {
        nameFee: strings.RTP_FEE,
        valueFee: invoicePreviewResponse?.paymentFees.rtpFee || 0,
      }
    }
  }
}

export const formatTermType = (type?: string) => {
  switch (type) {
    case 'INVOICE_RATE':
      return strings.INVOICE
    case 'NFE_RATE':
      return strings.TERM_TYPE_NFE
    case 'ADVANCE_RATE':
      return strings.TERM_TYPE_ADVANCE
    default:
      return ''
  }
}

export const formatNfeRateType = (type?: string) => {
  switch (type) {
    case 'FLAT_RATE':
      return strings.FLAT_RATE
    case 'PRIME_PLUS':
      return strings.PRIME_PLUS
    case 'LIBOR':
      return strings.LIBOR
    case 'SOFR':
      return strings.SOFR
    default:
      return ''
  }
}

export const formatTermResponse = (terms?: TermSummaryInfo[]) => {
  const modifiedData = terms?.map((term) => {
    return {
      ...term,
      interest: term.interest ? strings.YES : strings.NO,
      type: formatTermType(term.type),
    }
  })
  return modifiedData
}

export const formatDaysPer = (type?: string) => {
  switch (type) {
    case 'DAYS_365':
      return '365'
    case 'DAYS_360':
      return '360'
    default:
      return ''
  }
}

export const formatFloatDaysAre = (type?: string) => {
  switch (type) {
    case 'BUSINESS_DAYS':
      return strings.BUSINESS_DAYS
    case 'CALENDAR_DAYS':
      return strings.CALENDAR_DAYS
    case 'MONDAY_FRIDAY':
      return strings.MONDAY_FRIDAY
    default:
      return ''
  }
}

export const formatInvoiceGroupType = (type?: string) => {
  switch (type) {
    case 'INVOICE':
      return strings.INVOICE
    case 'FUEL_ADVANCE':
      return strings.FUEL_ADVANCE
    case 'VENDOR':
      return strings.VENDOR
    case 'BROKER':
      return strings.BROKER
    default:
      return ''
  }
}

export const formatUserResponse = (users?: UserInfo[]) => {
  const modifiedData = users?.map((user) => {
    return {
      ...user,
      id: user.id,
      fullName: user.firstName.concat(' ', user.lastName),
      email: user.email,
      role: user.role.name,
    }
  })
  return modifiedData
}

export const formatClientUserResponse = (users?: UserInfo[]) => {
  const modifiedData = users?.map((user) => {
    return {
      ...user,
      id: user.id,
      fullName: user.firstName.concat(' ', user.lastName),
      email: user.email,
    }
  })
  return modifiedData
}

export const formatPermissionResponse = (data: any) => {
  let groupedData: any = {}
  let sensitiveGroupCodes: any = {}

  data.forEach((item: any) => {
    if (item.sensitive) {
      // Check if the item is sensitive and add it to the sensitiveGroupCodes
      if (!sensitiveGroupCodes[item.groupCode]) {
        sensitiveGroupCodes[item.groupCode] = []
      }
      sensitiveGroupCodes[item.groupCode].push(item)
    } else {
      // Check if the groupCode is already a key in the groupedData object
      if (!groupedData[item.groupCode]) {
        // If not, create an array under that key
        groupedData[item.groupCode] = []
      }

      // Push the current item to the array under the corresponding groupCode

      groupedData[item.groupCode].push(item)
    }
  })

  return { groupedData, sensitiveGroupCodes }
}

export const formatPayloadFundingInstructions = (
  data: FieldValues,
  paymentMethod: string,
  hasIntermediateBank: boolean,
  enablePreferredPaymentChannel: boolean
) => {
  const payload: FundingInstructionsUpdateRequest | FundingInstructionsCreateRequest = {
    name: data.name,
    payTo: data.payTo,
    paymentMethod: data.paymentMethod,
    ...(enablePreferredPaymentChannel && { preferredPaymentChannel: data.fundingServices }),
  }

  if (
    paymentMethod === 'FUEL_CARD' ||
    paymentMethod === 'CHECK' ||
    paymentMethod === 'MONEY_CODE' ||
    paymentMethod === 'DIGITAL_WALLET'
  ) {
    payload.specialInstructions = data.specialInstructions
    if (paymentMethod === 'FUEL_CARD') {
      payload.fuelCardNumber = data.fuelCardNumber
    } else if (paymentMethod === 'CHECK') {
      payload.checkCardNumber = data.checkCardNumber
    } else if (paymentMethod === 'MONEY_CODE') {
      payload.moneyCodeAccountNumber = data.moneyCodeAccountNumber
    } else if (paymentMethod === 'DIGITAL_WALLET') {
      payload.digitalWalletAccountNumber = data.digitalWalletAccountNumber
    }
  } else {
    payload.address1 = data.address1
    payload.address2 = data.address2
    payload.city = data.city
    payload.stateCode = data.stateCode
    payload.zipCode = data.zipCode
    payload.countryCode = getCreatableValue(data.countryCode)
    payload.contactName = data.contactName
    payload.contactPhone = data.contactPhone
    payload.bankName = data.bankName
    payload.bankAccountType = data.bankAccountType ? data.bankAccountType.toUpperCase() : null
    payload.bankAccountNumber = data.bankAccountNumber
    payload.routingNumber = data.routingNumber
    payload.bankCode = data.bankCode
    payload.swiftCode = data.swiftCode
    payload.bankCity = data.bankCity
    payload.bankState = data.bankState
    payload.bankCountry = getCreatableValue(data.bankCountry)
    payload.specialInstructions = data.specialInstructions
    payload.hasIntermediateBank = hasIntermediateBank

    if (hasIntermediateBank) {
      payload.intermediateBankName = data.intermediateBankName
      payload.intermediateAccountNumber = data.intermediateAccountNumber
      payload.intermediateRoutingNumber = data.intermediateRoutingNumber
      payload.intermediateBankCode = data.intermediateBankCode
      payload.intermediateSwiftCode = data.intermediateSwiftCode
      payload.intermediateBankCity = data.intermediateBankCity
      payload.intermediateBankState = data.intermediateBankState
      payload.intermediateBankCountry = getCreatableValue(data.intermediateBankCountry)
      payload.intermediateSpecialInstructions = data.intermediateSpecialInstructions
    }
  }

  return payload
}

export const isPaymentMethod = (paymentMethod?: string): boolean => {
  return (
    paymentMethod === 'ACH' ||
    paymentMethod === 'CHECK' ||
    paymentMethod === 'WIRE' ||
    paymentMethod === 'FUEL_CARD' ||
    paymentMethod === 'SAME_DAY_ACH' ||
    paymentMethod === 'MONEY_CODE' ||
    paymentMethod === 'DIGITAL_WALLET' ||
    paymentMethod === 'INSTANT' ||
    paymentMethod === 'RTP'
  )
}

export const getColorFromPercentage = (percentage: string) => {
  // Remove any leading or trailing whitespace
  percentage = percentage.trim()[0]

  // Check if the percentage string starts with a '+' or '-'
  if (percentage === '-') {
    return colors.red60 // Negative value, return red
  } else if (percentage === '+') {
    return colors.green60 // Positive value, return green
  } else {
    // Handle the case where there is no '+' or '-' sign (considered positive)
    return colors.green60
  }
}

export function formatClassicGlCodeTypeLabel(glCodeType: string): string {
  return capitalizeWords(glCodeType.replace(/([A-Z])/g, ' $1'))
}

export function transformGlCodesToOptions(glCodesResponse?: Record<string, any>): DropDownOptions[] {
  const feeOptions: DropDownOptions[] = []

  for (const key in glCodesResponse) {
    if (glCodesResponse[key] !== null) {
      const label = `${formatClassicGlCodeTypeLabel(key)} - ${glCodesResponse[key]}`

      feeOptions.push({
        label: label,
        value: key,
      })
    }
  }

  return feeOptions
}

// Which Ledger* table(s) each classic GL code purpose actually gets written to, verified against
// every `new LedgerXXX()` builder in commons' LedgerFactory.java (not guessed). A purpose
// legitimately used by more than one ledger (e.g. collectionFee, escrowRelease, the paymentFee*
// family) lists every one of them on purpose — grouped dropdowns show the same option once per
// group it belongs to. A custom code inherits its parentGlCodeType's group(s) the same way.
const GL_CODE_TYPE_LEDGER_GROUPS: Record<string, string[]> = {
  fundedInvoice: ['AR'],
  paidInvoice: ['AR'],
  partialPayment: ['AR', 'CASH_RESERVE'],
  assignPaymentToInvoice: ['AR_BANK_ACCOUNT'],
  fundInvoiceGroups: ['AP_BANK_ACCOUNT'],
  accruedFee: ['ACCRUED_FEE'],
  minimumCollectionFee: ['ACCRUED_FEE'],
  collectionFee: ['ACCRUED_FEE', 'CASH_RESERVE', 'ESCROW', 'FEE'],
  accruedInterest: ['ACCRUED_INTEREST'],
  chargebackPartial: ['CASH_RESERVE'],
  chargebackFull: ['CASH_RESERVE'],
  overPayment: ['CASH_RESERVE'],
  cashReserveOOSInvoice: ['CASH_RESERVE'],
  escrowRelease: ['CASH_RESERVE', 'ESCROW'],
  escrowReserve: ['CASH_RESERVE', 'ESCROW'],
  splitEscrowReserve: ['ESCROW'],
  purchaseFee: ['FEE'],
  additionalFees: ['FEE'],
  overAdvanceFee: ['FEE'],
  paymentFeeAch: ['FEE', 'CASH_RESERVE'],
  paymentFeeWire: ['FEE', 'CASH_RESERVE'],
  paymentFeeCheck: ['FEE', 'CASH_RESERVE'],
  paymentFeeFuelCard: ['FEE', 'CASH_RESERVE'],
  paymentFeeSDAch: ['FEE', 'CASH_RESERVE'],
  paymentFeeDigitalWallet: ['FEE', 'CASH_RESERVE'],
  paymentFeeInstant: ['FEE', 'CASH_RESERVE'],
  paymentFeeRtp: ['FEE', 'CASH_RESERVE'],
  paymentFeeMoneyCode: ['FEE', 'CASH_RESERVE'],
  fuelAdvanceRecovery: ['FUEL_ADVANCE'],
  paidOverAdvance: ['OVER_ADVANCE'],
  unappliedPayment: ['UNAPPLIED'],
  appliedPayment: ['UNAPPLIED'],
  writeOffs: ['WRITE_OFFS'],
  // feePerInvoice, negativeInterestFee, paidFuelAdvanceRecovery: configured on the factor's GL
  // code table, but no LedgerFactory builder currently writes them to any ledger — Uncategorized.
}

function glCodeGroupKeysFor(glCodeType?: string): string[] {
  if (!glCodeType) return ['UNCATEGORIZED']
  return GL_CODE_TYPE_LEDGER_GROUPS[glCodeType] || ['UNCATEGORIZED']
}

// Computed fresh per call (not a module-level const) so it follows the active locale, same as
// ledgersTypeDropdownOptions does for its labels.
function getGlCodeGroupLabels(): Record<string, string> {
  return {
    AR: strings.ACCOUNTS_RECEIVABLE,
    AR_BANK_ACCOUNT: strings.AR_BANK_ACCOUNT,
    AP_BANK_ACCOUNT: strings.AP_BANK_ACCOUNT,
    CASH_RESERVE: strings.CASH_RESERVE,
    ACCRUED_FEE: strings.ACCRUED_FEE,
    ACCRUED_INTEREST: strings.ACCRUED_INTEREST,
    ESCROW: strings.ESCROW_RESERVE,
    FEE: strings.FEE,
    FUEL_ADVANCE: strings.FUEL_ADVANCE,
    OVER_ADVANCE: strings.OVER_ADVANCE,
    UNAPPLIED: strings.UNAPPLIED,
    WRITE_OFFS: strings.WRITE_OFFS,
    UNCATEGORIZED: strings.PARENT_CLASSIC_GL_CODE_UNCATEGORIZED,
    CUSTOM_UNCATEGORIZED: strings.OTHER_CUSTOM_GL_CODES,
  }
}

// Group display order is alphanumeric by label (not a fixed list), so it naturally follows locale.
function getSortedGlCodeGroupKeys(labels: Record<string, string>): string[] {
  return Object.keys(labels).sort((a, b) => labels[a].localeCompare(labels[b]))
}

function pushIntoGroups(
  entriesByGroup: Record<string, DropDownOptions[]>,
  labels: Record<string, string>,
  option: DropDownOptions,
  groups: string[]
) {
  for (const group of groups) {
    entriesByGroup[group] = entriesByGroup[group] || []
    entriesByGroup[group].push({ ...option, group: labels[group] })
  }
}

// Groups are ordered alphanumerically by label, and so are the options within each group.
function flattenGroupsSorted(entriesByGroup: Record<string, DropDownOptions[]>, labels: Record<string, string>): DropDownOptions[] {
  return getSortedGlCodeGroupKeys(labels).flatMap((group) =>
    (entriesByGroup[group] || []).slice().sort((a, b) => a.label.localeCompare(b.label))
  )
}

// One entry per (glCodeType, ledger group) pair. A group with no matching glCodeType simply never
// appears — no empty headers. Used by the "Parent Classic GL Code" picker in the Custom GL Code CRUD.
export function transformGlCodesToGroupedOptions(glCodesResponse?: Record<string, any>): DropDownOptions[] {
  const labels = getGlCodeGroupLabels()
  const entriesByGroup: Record<string, DropDownOptions[]> = {}

  for (const option of transformGlCodesToOptions(glCodesResponse)) {
    pushIntoGroups(entriesByGroup, labels, option, glCodeGroupKeysFor(option.value))
  }

  return flattenGroupsSorted(entriesByGroup, labels)
}

export function transformCustomGlCodesToOptions(customGlCodes?: CustomGlCodeInfo[]): DropDownOptions[] {
  if (!customGlCodes) return []

  return customGlCodes.map((customGlCode) => ({
    value: customGlCode.id,
    label: `${customGlCode.name} - ${customGlCode.code} (${strings.CUSTOM_GL_CODE_OPTION_LABEL})`,
  }))
}

// Internal-only: carries the classic gl code type (matching a classic entry's `value` within the
// same group) that a custom code was configured with, so the group can be re-flattened with the
// custom code nested right under its classic parent. Not part of the public `DropDownOptions`
// contract — never returned outside this file.
type GroupedGlCodeOption = DropDownOptions & { parentValue?: string }

// Classic purposes and custom codes merged into a single grouped list (same groups as
// transformGlCodesToGroupedOptions) — a custom code is grouped by its own parentGlCodeType,
// falling into Uncategorized when it has none. Used by the GL Code dropdown on
// LedgersCreateManualTransactionBlade (both the main and offsetting entry).
export function transformGlCodesAndCustomGlCodesToGroupedOptions(
  glCodesResponse?: Record<string, any>,
  customGlCodes?: CustomGlCodeInfo[]
): DropDownOptions[] {
  const labels = getGlCodeGroupLabels()
  const entriesByGroup: Record<string, GroupedGlCodeOption[]> = {}

  for (const option of transformGlCodesToOptions(glCodesResponse)) {
    pushIntoGroups(entriesByGroup, labels, option, glCodeGroupKeysFor(option.value))
  }

  const customGlCodeById = new Map((customGlCodes || []).map((customGlCode) => [customGlCode.id, customGlCode]))
  for (const option of transformCustomGlCodesToOptions(customGlCodes)) {
    const customGlCode = customGlCodeById.get(option.value)
    // A custom code with no parent gets its own "Custom GL Codes" group instead of falling into
    // the classic "Uncategorized" bucket, which reads as an error rather than an intentional choice.
    const groups = customGlCode?.parentGlCodeType
      ? glCodeGroupKeysFor(customGlCode.parentGlCodeType)
      : ['CUSTOM_UNCATEGORIZED']
    const optionWithParent: GroupedGlCodeOption = { ...option, parentValue: customGlCode?.parentGlCodeType }
    pushIntoGroups(entriesByGroup, labels, optionWithParent, groups)
  }

  return flattenGroupsWithClassicParentsSorted(entriesByGroup, labels)
}

// Within each group: entries whose `parentValue` matches another entry's `value` in that same
// group are pulled out of the plain alphabetical order and reinserted immediately after that
// parent entry (siblings sorted alphabetically among themselves), tagged with `indentLevel: 1` so
// DropdownField renders them visually nested. A `parentValue` that doesn't resolve to any entry in
// the group (parent not configured for this factor, or no parent at all) is left in the plain
// alphabetical order untouched — same as `flattenGroupsSorted`.
function reorderGroupWithClassicParents(groupEntries: GroupedGlCodeOption[]): DropDownOptions[] {
  const sorted = groupEntries.slice().sort((a, b) => a.label.localeCompare(b.label))
  const valuesInGroup = new Set(sorted.map((entry) => entry.value))

  const childrenByParentValue = new Map<string, GroupedGlCodeOption[]>()
  const remaining: GroupedGlCodeOption[] = []

  for (const entry of sorted) {
    const isResolvableChild = isDefined(entry.parentValue) && valuesInGroup.has(entry.parentValue as string)
    if (isResolvableChild) {
      const siblings = childrenByParentValue.get(entry.parentValue as string) || []
      siblings.push(entry)
      childrenByParentValue.set(entry.parentValue as string, siblings)
    } else {
      remaining.push(entry)
    }
  }

  Array.from(childrenByParentValue.values()).forEach((siblings) => {
    siblings.sort((a, b) => a.label.localeCompare(b.label))
  })

  return remaining.flatMap((entry) => {
    const children = childrenByParentValue.get(entry.value)
    if (!children || children.length === 0) return [entry]
    return [entry, ...children.map((child) => ({ ...child, indentLevel: 1 }))]
  })
}

function flattenGroupsWithClassicParentsSorted(
  entriesByGroup: Record<string, GroupedGlCodeOption[]>,
  labels: Record<string, string>
): DropDownOptions[] {
  return getSortedGlCodeGroupKeys(labels).flatMap((group) => reorderGroupWithClassicParents(entriesByGroup[group] || []))
}

function capitalizeWords(str: string): string {
  return str.replace(/\b\w/g, (match) => match.toUpperCase())
}

export function capitalizeCamelCase(str: string): string {
  if (!isDefined(str)) return ''
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, (match) => match.toUpperCase())
}

type Summary = {
  id: string
  summaryName: string
  startBalance: number
  endBalance: number
  balanceChange: number
}

type SummaryArray = {
  data: Summary[]
  portfolioTotalsInfo: {
    beginningBalance: number
    endingBalance: number
    balanceChange: number
  }
}

export type CcBcc = {
  id: string
  email: string
  type: string
}

export type CcBccArray = {
  data: CcBcc[]
}

export function summaryToArray(summary: any): SummaryArray {
  const summaryArray: SummaryArray = {
    data: [],
    portfolioTotalsInfo: {
      beginningBalance: 0,
      endingBalance: 0,
      balanceChange: 0,
    },
  }

  for (const key in summary) {
    if (summary.hasOwnProperty(key)) {
      const summaryItem = summary[key]
      summaryArray.portfolioTotalsInfo.beginningBalance += summaryItem.startBalance
      summaryArray.portfolioTotalsInfo.endingBalance += summaryItem.endBalance
      summaryArray.portfolioTotalsInfo.balanceChange += summaryItem.balanceChange

      summaryArray.data.push({
        id: uuid(),
        summaryName: key,
        startBalance: summaryItem.startBalance,
        endBalance: summaryItem.endBalance,
        balanceChange: summaryItem.balanceChange,
      })
    }
  }

  return summaryArray
}

export function bccCcToArray(ccEmailList: string, bccEmailList: string): CcBccArray {
  const ccBccArray: CcBccArray = {
    data: [],
  }

  if (isDefined(ccEmailList) && ccEmailList.length > 0) {
    ccEmailList.split(',').forEach((email) => {
      ccBccArray.data.push({
        id: uuid(),
        email: email,
        type: 'CC',
      })
    })
  }
  if (isDefined(bccEmailList) && bccEmailList.length > 0) {
    bccEmailList.split(',').forEach((email) => {
      ccBccArray.data.push({
        id: uuid(),
        email: email,
        type: 'BCC',
      })
    })
  }

  return ccBccArray
}

type Ledger = {
  id: string
  ledgerName: string
  startBalance: number
  endBalance: number
  totalCredit: number
  totalDebit: number
  balanceChange: number
}

type LedgersArray = {
  data: Ledger[]
  ledgersTotalsInfo: {
    totalCredit: number
    totalDebit: number
    beginningBalance: number
    endingBalance: number
    balanceChange: number
  }
}

export function ledgersToArray(ledgers: any): LedgersArray {
  const ledgersArray: LedgersArray = {
    data: [],
    ledgersTotalsInfo: {
      totalCredit: 0,
      totalDebit: 0,
      beginningBalance: 0,
      endingBalance: 0,
      balanceChange: 0,
    },
  }

  for (const key in ledgers) {
    if (ledgers.hasOwnProperty(key)) {
      const ledger = ledgers[key]
      ledgersArray.ledgersTotalsInfo.totalCredit += ledger.totalCredit
      ledgersArray.ledgersTotalsInfo.totalDebit += ledger.totalDebit
      ledgersArray.ledgersTotalsInfo.beginningBalance += ledger.startBalance
      ledgersArray.ledgersTotalsInfo.endingBalance += ledger.endBalance
      ledgersArray.ledgersTotalsInfo.balanceChange += ledger.balanceChange

      ledgersArray.data.push({
        id: uuid(),
        ledgerName: key,
        startBalance: ledger.startBalance,
        endBalance: ledger.endBalance,
        totalCredit: ledger.totalCredit,
        totalDebit: ledger.totalDebit,
        balanceChange: ledger.balanceChange,
      })
    }
  }

  return ledgersArray
}

export function formatLedgerName(ledgerName: string) {
  switch (ledgerName) {
    case 'openAr':
      return strings.OPEN_AR
    case 'fees':
      return strings.FEES
    case 'escrow':
      return strings.ESCROW
    case 'cashReserve':
      return strings.CASH_RESERVE
    case 'apBankAccount':
      return strings.AP_BANK_ACCOUNT
    case 'arBankAccount':
      return strings.AR_BANK_ACCOUNT
    case 'nfe':
      return strings.NFE
    case 'fuelAdvance':
      return strings.FUEL_ADVANCE
    case 'liability':
      return strings.LIABILITY
    case 'unapplied':
      return strings.UNAPPLIED
    case 'overAdvance':
      return strings.OVER_ADVANCE
    default:
      return ''
  }
}

export function formatLedgerType(ledgerType: string) {
  switch (ledgerType) {
    case 'AR':
      return strings.ACCOUNTS_RECEIVABLE
    case 'FEE':
      return strings.FEE
    case 'ACCRUED_FEE':
      return strings.ACCRUED_FEE
    case 'ESCROW':
      return strings.ESCROW_RESERVE
    case 'CASH_RESERVE':
      return strings.CASH_RESERVE
    case 'ACCRUED_INTEREST':
      return strings.ACCRUED_INTEREST
    case 'AP_BANK_ACCOUNT':
      return strings.AP_BANK_ACCOUNT
    case 'AR_BANK_ACCOUNT':
      return strings.AR_BANK_ACCOUNT
    case 'LIABILITY':
      return strings.LIABILITY
    case 'UNAPPLIED':
      return strings.UNAPPLIED
    case 'WRITE_OFFS':
      return strings.WRITE_OFFS
    case 'OVER_ADVANCE':
      return strings.OVER_ADVANCE
    default:
      return ''
  }
}

export function formatSpecialSorting(sort: string) {
  switch (sort) {
    case 'clientName':
      return 'companyClientName'
    case 'debtorName':
      return 'companyDebtorName'
    default:
      return sort
  }
}

export function generatePaymentMethodOptions(filterValues?: string[]): DropDownOptions[] {
  const allOptions: DropDownOptions[] = [
    { value: '', label: '-' },
    { value: 'ACH', label: formatPaymentMethod('ACH') },
    { value: 'CHECK', label: formatPaymentMethod('CHECK') },
    { value: 'FUEL_CARD', label: formatPaymentMethod('FUEL_CARD') },
    { value: 'SAME_DAY_ACH', label: formatPaymentMethod('SAME_DAY_ACH') },
    { value: 'WIRE', label: formatPaymentMethod('WIRE') },
    { value: 'MONEY_CODE', label: formatPaymentMethod('MONEY_CODE') },
    { value: 'DIGITAL_WALLET', label: formatPaymentMethod('DIGITAL_WALLET') },
    { value: 'INSTANT', label: formatPaymentMethod('INSTANT') },
    { value: 'RTP', label: formatPaymentMethod('RTP') },
  ]

  if (filterValues && filterValues.length > 0) {
    return allOptions.filter((option) => !filterValues.includes(option.value))
  }
  return allOptions
}

export function addPaymentOption(paymentOptionsArray: DropDownOptions[], newOptionValue: string) {
  if (newOptionValue === 'ACH') return [...paymentOptionsArray, { value: 'ACH', label: formatPaymentMethod('ACH') }]
  if (newOptionValue === 'CHECK')
    return [...paymentOptionsArray, { value: 'CHECK', label: formatPaymentMethod('CHECK') }]
  if (newOptionValue === 'FUEL_CARD')
    return [...paymentOptionsArray, { value: 'FUEL_CARD', label: formatPaymentMethod('FUEL_CARD') }]
  if (newOptionValue === 'SAME_DAY_ACH')
    return [...paymentOptionsArray, { value: 'SAME_DAY_ACH', label: formatPaymentMethod('SAME_DAY_ACH') }]
  if (newOptionValue === 'WIRE') return [...paymentOptionsArray, { value: 'WIRE', label: formatPaymentMethod('WIRE') }]
  if (newOptionValue === 'MONEY_CODE')
    return [...paymentOptionsArray, { value: 'MONEY_CODE', label: formatPaymentMethod('MONEY_CODE') }]
  if (newOptionValue === 'DIGITAL_WALLET')
    return [...paymentOptionsArray, { value: 'DIGITAL_WALLET', label: formatPaymentMethod('DIGITAL_WALLET') }]
  if (newOptionValue === 'INSTANT')
    return [...paymentOptionsArray, { value: 'INSTANT', label: formatPaymentMethod('INSTANT') }]
  if (newOptionValue === 'RTP') return [...paymentOptionsArray, { value: 'RTP', label: formatPaymentMethod('RTP') }]

  return paymentOptionsArray
}

export const formattedCompanySortedValue = (value: string) => {
  if (value === 'companyName') return 'baseCompany.name'
  return `baseCompany.${value}`
}

export const toCamelCase = (str: string) => {
  if (isDefined(str)) {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return ''
}

export function handleAccruedInterestValue(companySettings?: CompanyClientAdminSettingsResponse) {
  const value =
    companySettings?.changeClientAccruedInterest !== null ? companySettings?.changeClientAccruedInterest : null
  return value === null ? '-' : value ? strings.YES : strings.NO
}

export function handleFrecuencyOfCollectionValue(companySettings?: CompanyClientAdminSettingsResponse) {
  const value =
    companySettings?.clientFrequencyOfCollectionOfAccruedFees !== null
      ? companySettings?.clientFrequencyOfCollectionOfAccruedFees
      : null

  return value === null ? '-' : value
}

export function validateIfClientSettingIsNull(companySetting?: CompanyClientAdminSettingsResponse) {
  const clientFrecuency = companySetting?.clientFrequencyOfCollectionOfAccruedFees === null
  const clientAccruedInterest = companySetting?.changeClientAccruedInterest === null

  return clientFrecuency || clientAccruedInterest
}

export function handleAccruedInterestBrokerValue(brokerSettings?: BrokerInfo) {
  const value = brokerSettings?.autochargeInterestFee !== null ? brokerSettings?.autochargeInterestFee : null
  return value === null ? '-' : value ? strings.YES : strings.NO
}

export function handleFrecuencyOfCollectionBrokerValue(brokerSettings?: BrokerInfo) {
  const value =
    brokerSettings?.frequencyOfAutochargeInterestFee !== null ? brokerSettings?.frequencyOfAutochargeInterestFee : null

  return value === null ? '-' : value
}

export function validateIfClientSettingBrokerIsNull(brokerSettings?: BrokerInfo) {
  const clientFrecuency = brokerSettings?.frequencyOfAutochargeInterestFee === null
  const clientAccruedInterest = brokerSettings?.autochargeInterestFee === null

  return clientFrecuency || clientAccruedInterest
}

export const formatOverAdvanceType = (overAdvanceType?: string | null) => {
  if (overAdvanceType === null) {
    return '-'
  }
  if (overAdvanceType === 'CASH_RESERVE') return strings.CASH_RESERVE
  if (overAdvanceType === 'BROKER_PAYMENT') return strings.BROKER_PAYMENT
}

export const chipColorFormat = (invoiceGroupType: string) => {
  if (invoiceGroupType === 'INVOICE') return 'info'
  if (invoiceGroupType === 'FUEL_ADVANCE') return 'error'
  if (invoiceGroupType === 'BROKER') return 'warning'
  if (invoiceGroupType === 'VENDOR') return 'success'
  if (invoiceGroupType === 'RESERVE_RELEASE') return 'primary'
  return 'default'
}

export const companyTypeChipColorFormat = (type: string) => {
  if (type === 'BROKER') return 'error'
  if (type === 'CLIENT') return 'info'
  if (type === 'DEBTOR') return 'success'
  if (type === 'CLIENT_DEBTOR') return 'primary'
  if (type === 'BROKER_DEBTOR') return 'warning'
  return 'default'
}

export const creditRequestTypeLabelFormat = (type: string) => {
  if (type === 'CLIENT') return strings.CLIENT
  if (type === 'BROKER') return strings.BROKER
  if (type === 'DEBTOR') return strings.DEBTOR
  if (type === 'CLIENT_DEBTOR') return strings.CLIENT_DEBTOR
  if (type === 'BROKER_DEBTOR') return strings.BROKER_DEBTOR
  return type
}

export const chipLabelFormat = (invoiceGroupType: string) => {
  if (invoiceGroupType === 'INVOICE') return strings.INVOICE
  if (invoiceGroupType === 'FUEL_ADVANCE') return strings.FUEL_ADVANCE
  if (invoiceGroupType === 'BROKER') return strings.BROKER
  if (invoiceGroupType === 'VENDOR') return strings.VENDOR
  if (invoiceGroupType === 'RESERVE_RELEASE') return strings.RESERVE_RELEASE
  if (invoiceGroupType === 'OVER_ADVANCE') return strings.OVER_ADVANCE
  return ''
}

export const invoicePaymentTypeFormat = (invoicePaymentType: string) => {
  switch (invoicePaymentType) {
    case 'FULL':
      return strings.FULL
    case 'PARTIAL':
      return strings.SHORT
    case 'OVER':
      return strings.OVER
    case 'CLOSED_PAID':
      return strings.CLOSED_PAID
    case 'PARTIAL_FULL':
      return strings.SHORT_FULL
    default:
      return '-' //! This should never happen
  }
}

export const getColumOrderByLedgerType = (ledgerType: string) => {
  switch (ledgerType) {
    case 'accounts-receivable':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'fee':
    case 'fee-escrow':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, overAdvanceReferenceNumber, reserveReleaseCode, fuelAdvanceReferenceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'accrued-fee':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'accrued-interest':
      return 'createdOn, transactionCode, companyClientName, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'escrow-reserve':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'cash-reserve':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, reserveReleaseCode, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'ap-bank-account':
      return 'createdOn, transactionCode, companyClientName, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'ar-bank-account':
      return 'createdOn, transactionCode, companyClientName, paymentTransactionRef, description, account, increaseAmount, decreaseAmount, glCode, notes'
    case 'liability':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'unapplied':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, invoiceGroupCode, paymentTransactionRef, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'fuel-advance':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, fuelAdvanceReferenceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'manual-transaction':
      return 'createdOn, clientName, ledgerType, offsettingEntries, amount, amountType, notes, authorName, glCode, active'
    case 'write-offs':
      return 'createdOn, transactionCode, companyClientName, invoiceDate, invoiceNumber, overAdvanceReferenceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    case 'over-advance':
      return 'createdOn, transactionCode, companyClientName, overAdvanceReferenceNumber, invoiceGroupCode, description, increaseAmount, decreaseAmount, glCode, notes'
    default:
      return ''
  }
}

export const orderLedgerColumns = (columns: string[], ledgerType: string): string[] => {
  const ledgerOrder = getColumOrderByLedgerType(ledgerType)
  if (ledgerOrder === '') return columns

  const orderedColumns = ledgerOrder.split(',').map((column) => column.trim())
  const orderedColumnsArray: string[] = []

  for (const column of orderedColumns) {
    const foundColumn = columns.find((c) => c.includes(column))
    if (foundColumn) {
      orderedColumnsArray.push(foundColumn)
    }
  }

  if (!orderedColumnsArray.includes('currency')) {
    const amountIndex = orderedColumnsArray.findIndex(
      (column) => column === 'increaseAmount' || column === 'decreaseAmount' || column === 'amount'
    )
    if (amountIndex >= 0) {
      orderedColumnsArray.splice(amountIndex, 0, 'currency')
    } else {
      orderedColumnsArray.push('currency')
    }
  }

  return orderedColumnsArray
}

export const ucfirst = (str: string) => {
  const lowerStr = str.toLowerCase()
  return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1)
}

export const formatLedgerNameKey = (keyId: string): string => {
  switch (keyId) {
    case 'openArKey':
      return strings.OPEN_AR_KEY
    case 'feesKey':
      return strings.FEES_KEY
    case 'accruedFeesKey':
      return strings.ACCRUED_FEES_KEY
    case 'accruedInterestKey':
      return strings.ACCRUED_INTEREST_KEY
    case 'escrowReserveKey':
      return strings.ESCROW_RESERVE_KEY
    case 'cashReserveKey':
      return strings.CASH_RESERVE_KEY
    case 'apBankAccountKey':
      return strings.AP_BANK_ACCOUNT_KEY
    case 'arBankAccountKey':
      return strings.AR_BANK_ACCOUNT_KEY
    case 'liabilityKey':
      return strings.LIABILITY_KEY
    case 'unappliedKey':
      return strings.UNNAPLIED_KEY
    case 'fuelAdvanceKey':
      return strings.FUEL_ADVANCE_KEY
    case 'overAdvanceKey':
      return strings.OVER_ADVANCE_KEY
    default:
      return keyId
  }
}

export const formattedCreditRequestSortedValue = (value: string) => {
  const atributeNeedsUserPrefix = ['userEmail', 'username']

  if (atributeNeedsUserPrefix.includes(value)) {
    if (value === 'userEmail') return 'user.email'
    if (value === 'username') return 'user.firstName'
  }

  return value
}

export const formattedDebtorRequestSortedValue = (value: string) => {
  const attributeNeedsUserPrefix = ['userEmail', 'username', 'debtorName', 'country', 'state', 'zip', 'debtorEmail']

  if (attributeNeedsUserPrefix.includes(value)) {
    if (value === 'userEmail') return 'user.email'
    if (value === 'username') return 'user.firstName'
    if (value === 'debtorName') return 'companyName'
    if (value === 'country') return 'countryCode'
    if (value === 'state') return 'stateCode'
    if (value === 'zip') return 'zipCode'
    if (value === 'debtorEmail') return 'email'
  }

  return value
}

export const formattedInternalCreditRequestSortedValue = (value: string) => {
  const attributeNeedsUserPrefix = ['companyName', 'type', 'requestedByUsername', 'updatedByUsername']

  if (attributeNeedsUserPrefix.includes(value)) {
    if (value === 'companyName') return 'cachedCompanyName'
    if (value === 'type') return 'type'
    if (value === 'requestedByUsername') return 'requestedBy.firstName'
    if (value === 'updatedByUsername') return 'updatedBy.firstName'
  }

  return value
}

export const isBaseVerificationMethod = (methodName: string): boolean => {
  return methodName === 'EMAIL' || methodName === 'MAIL' || methodName === 'ONLINE PORTAL' || methodName === 'PHONE'
}

export const formatOutOfSystemInvoices = (outOfSystemInvoices: OutOfSystemInvoicesInfo[]) => {
  const transformedInvoices: InvoiceSummaryInfo[] = []
  const outInvoicesIds: string[] = []

  outOfSystemInvoices.forEach((invoice) => {
    transformedInvoices.push({
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      referenceNumber: invoice.referenceNumber,
      companyClientId: invoice.companyClientId,
      companyClientName: invoice.companyClientName,
      companyDebtorId: invoice.companyDebtorId,
      companyDebtorName: invoice.companyDebtorName,
      termId: 'default-term-id',
      termName: 'default-term-name',
      status: 'PENDING',
      invoiceAmount: invoice.invoiceAmount,
      invoiceBalance: invoice.invoiceAmount,
      invoiceDate: invoice.invoiceDate.toString(),
      closedDate: invoice.invoiceDate.toString(),
      callBackDate: invoice.invoiceDate.toString(),
      willPayDate: invoice.invoiceDate.toString(),
      notes: '',
      documents: [],
      verificationStatus: 'NOT_VERIFIED',
      changelog: [],
      ineligible: false,
      disputed: false,
      createdOn: new Date().toISOString(),
      purchaseFeeAmount: 0,
      escrowReserveAmount: 0,
      cashReserveAmount: 0,
      advanceAmount: 0,
      daysAged: 0,
      daysOutstanding: 0,
      paidDate: '',
      paymentStatus: 'Open',
      fundedDate: '',
      paymentTransactionNumber: [],
      cashEscrowAmount: 0,
      fuelAdvanceId: '',
      fuelAdvanceReferenceNumber: '',
      fuelAdvanceAmount: 0,
      forcedTermId: '',
      forcedTermName: '',
      companyBrokerId: invoice.companyBrokerId,
      companyBrokerName: invoice.companyBrokerName,
      invoiceType: invoice.type,
      vendorPaymentAmount: 0,
      vendorInvoiceId: invoice.companyVendorId,
      vendorAdvanceAmount: 0,
      quickPayFeeAmount: 0,
      brokerQuickPayFeeAmount: 0,
      factorQuickPayFeeAmount: 0,
      quickPayFeeId: '',
      vendorPaymentDate: '',
      vendorFundingDate: '',
      companyVendorName: invoice.companyVendorName,
      companyVendorId: '',
      overAdvanceType: '',
      overAdvanceAmount: 0,
      snapshotTermPurchaseRate: 0,
      snapshotTermEscrowReserveRate: 0,
      nonFactoredInvoice: 'OOS',
      recourse: null,
      verificationMethod: '',
      isOutOfSystemInvoice: true,
      dueDate: null,
      updatedOn: null,
      hasCombinedDocument: false,
      hasBillingDocument: false,
      billingDocumentGeneratedAt: null,
    })
    outInvoicesIds.push(invoice.id)
  })

  return { transformedInvoices, outInvoicesIds }
}

export const formatEmailHistoryType = (type: string) => {
  switch (type) {
    case 'NOA':
      return strings.NOA
    case 'DEBTOR_REMITTANCE':
      return strings.DEBTOR_REMITTANCE
    case 'VENDOR_REMITTANCE':
      return strings.VENDOR_REMITTANCE
    case 'BROKER_REMITTANCE':
      return strings.BROKER_REMITTANCE
    case 'STATEMENT':
      return strings.STATEMENT
    case 'BILLING':
      return strings.BILLING
    case 'UPDATE_RELATIONSHIP_REQUEST':
      return strings.UPDATE_RELATIONSHIP_REQUEST
    case 'UPDATE_DEBTOR_REQUEST':
      return strings.UPDATE_DEBTOR_REQUEST
    case 'INVOICE_DOCUMENT_EXPORT':
      return strings.INVOICE_DOCUMENT_EXPORT
    default:
      return ''
  }
}

export function localDateTimeFormatWithDifferenceToday(date: string | null) {
  function calculateDaysDifference(date1: Date, date2: Date) {
    const oneDay = 24 * 60 * 60 * 1000
    const diffInTime = date2.getTime() - date1.getTime()
    return Math.round(diffInTime / oneDay)
  }

  if (date === null) {
    return null
  }

  const formattedDate = dateTimeFormat({
    date: date,
    formatOptions: {
      dateStyle: 'short',
      timeStyle: 'short',
    },
  })

  const inputDate = new Date(date)
  const currentDate = new Date()

  const differenceInDays = calculateDaysDifference(inputDate, currentDate)

  return {
    formattedDate: formattedDate,
    differenceInDays: differenceInDays,
  }
}

export const formatEmailHistoryStatus = (status: string) => {
  switch (status) {
    case 'PENDING':
      return strings.PENDING.SINGLE
    case 'SENT':
      return strings.SENT
    case 'SUBMITTED':
      return strings.SUBMITTED
    case 'OPENED':
      return strings.OPENED
    case 'CLICKED':
      return strings.CLICKED
    case 'FAILED':
      return strings.FAILED
    default:
      return ''
  }
}

export const formatEmailHistorySendType = (sendType: string) => {
  switch (sendType) {
    case 'RETRY':
      return strings.RETRY
    case 'SYSTEM':
      return strings.SYSTEM
    default:
      return ''
  }
}

export const formatEmployeeType = (employeeType: string) => {
  switch (employeeType) {
    case CompanyEmployeeEnum.ACCOUNT_EXECUTIVE:
      return strings.ACCOUNT_EXECUTIVE
    case CompanyEmployeeEnum.COLLECTION_AGENT:
      return strings.COLLECTION_AGENT
    case CompanyEmployeeEnum.SALES_EXECUTIVE:
      return strings.SALES_EXECUTIVE
    default:
      return ''
  }
}

type accountArray = {
  data: accountInfo[]
}

type accountInfo = {
  id: string
  accountName: string
}

export const paymentAccountsToArray = (paymentAccounts: String) => {
  const paymentAccountsArray: accountArray = {
    data: [],
  }
  if (isDefined(paymentAccounts) && paymentAccounts.length > 0) {
    paymentAccounts.split(',').forEach((account) => {
      paymentAccountsArray.data.push({
        id: uuid(),
        accountName: account,
      })
    })
  }
  return paymentAccountsArray
}

// "Lockbox" is always implicitly available (the backend prepends it to GET /factors/me/accounts
// and rejects it from the editable accounts list on save) — a factor with no accounts configured
// yet otherwise has no way to reach its GL Code Assignment blade, since the Payment Accounts
// table only lists what's literally in factor.accounts. Prepend a synthetic row when missing so
// it's always reachable, matching how the rest of the app already treats it.
export const withDefaultLockboxAccount = (paymentAccountsArray: accountArray): accountArray => {
  const hasLockbox = paymentAccountsArray.data.some((account) => account.accountName.toLowerCase() === 'lockbox')
  if (hasLockbox) return paymentAccountsArray
  return {
    data: [{ id: 'lockbox', accountName: 'Lockbox' }, ...paymentAccountsArray.data],
  }
}

export const fileSizeConverter = (sizeInBytes: number): string => {
  if (sizeInBytes < 0) {
    throw new Error('Size cannot be negative')
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let unitIndex = 0
  let convertedSize = sizeInBytes

  if (sizeInBytes < 1024) {
    return `${convertedSize.toFixed(2)} ${units[unitIndex]}`
  }

  while (convertedSize >= 1024 && unitIndex < units.length - 1) {
    convertedSize /= 1024
    unitIndex++
  }

  if (unitIndex === 2 && convertedSize < 0.01) {
    unitIndex = 1
    convertedSize = sizeInBytes / 1024
  }

  return `${convertedSize.toFixed(2)} ${units[unitIndex]}`
}

export const formatLabelEntityType = (entityType: string): string => {
  switch (entityType) {
    case 'INVOICE':
      return strings.INVOICE
    case 'COMPANY':
      return strings.COMPANY
    case 'PAYMENT':
      return strings.PAYMENT
    case 'TERM':
      return strings.TERM
    default:
      return entityType
  }
}

export const formatLabelCategory = (category: string): string => {
  switch (category) {
    case 'GENERAL':
      return strings.CATEGORY_GENERAL
    case 'INVOICE_COLLECTIONS':
      return strings.CATEGORY_INVOICE_COLLECTIONS
    case 'INVOICE_CHARGEBACK':
      return strings.CATEGORY_INVOICE_CHARGEBACK
    case 'INVOICE_PAYMENT':
      return strings.CATEGORY_INVOICE_PAYMENT
    default:
      return category
  }
}

export const formatUnitTime = (unitTime?: string) => {
  switch (unitTime) {
    case 'DAYS':
      return strings.DAYS
    case 'WEEKS':
      return strings.WEEKS
    case 'MONTHS':
      return strings.MONTHS
    default:
      return '-'
  }
}

export function getDraftPaymentStatusChipColor(status: DraftPaymentStatus) {
  switch (status) {
    case 'PENDING':
      return 'warning'
    case 'PROCESSING':
      return 'primary'
    case 'ERROR':
      return 'error'
    case 'FINISHED':
      return 'success'
    case 'WARNING':
      return 'warning'
    default:
      return 'default'
  }
}

export function isEmptyHtml(htmlContent: string | undefined): boolean {
  if (!htmlContent) return true

  const tempElement = document.createElement('div')
  tempElement.innerHTML = htmlContent

  const cleanedContent = tempElement.textContent || tempElement.innerText || ''

  return cleanedContent.trim() === ''
}

export const getAccountFieldName = (paymentMethod: string): string | undefined => {
  const accountFieldNames = new Map<string, string>([
    ['CHECK', 'checkCardNumber'],
    ['FUEL_CARD', 'fuelCardNumber'],
    ['MONEY_CODE', 'moneyCodeAccountNumber'],
    ['DIGITAL_WALLET', 'digitalWalletAccountNumber'],
  ])

  return accountFieldNames.get(paymentMethod)
}

export const getFundingInstructionDividerUtils = (paymentMethod: string, strings: any): string => {
  return paymentMethod === 'DIGITAL_WALLET' ? strings.WALLET_INFO : strings.CARD_INFO
}

export function sanitizeFilename(fileName: string): string {
  const notAllowedCharacters = /[^a-zA-Z0-9-]/g
  return fileName.replace(notAllowedCharacters, '')
}

export function formatReleaseType(releaseType?: string): string {
  switch (releaseType) {
    case 'HOLD':
      return strings.HOLD
    case 'RELEASE':
      return strings.RELEASE
    default:
      return ''
  }
}

export function formatFeeType(feeType?: string): string {
  switch (feeType) {
    case 'CHARGE':
    // Legacy values kept for records saved before the CHARGE/CREDIT rename.
    case 'FEE':
      return strings.CHARGE
    case 'CREDIT':
    case 'DISCOUNT':
      return strings.CREDIT
    default:
      return ''
  }
}

export function formatCompanyScheduledFeeStatus(status?: string): string {
  switch (status) {
    case 'PENDING':
      return strings.PENDING.SINGLE
    case 'APPROVED':
      return strings.APPROVED
    case 'COLLECTED':
      return strings.COLLECTED
    default:
      return '-'
  }
}

export function formatScheduleType(scheduleType?: string): string {
  switch (scheduleType) {
    case 'FEE':
      return strings.FEE
    case 'CASH_RESERVE':
      return strings.CASH_RESERVE
    default:
      return '-'
  }
}

export function formatCashReserveType(cashReserveType?: string): string {
  switch (cashReserveType) {
    case 'HOLD':
      return strings.HOLD
    case 'RELEASE':
      return strings.RELEASE
    default:
      return '-'
  }
}

export function formatNoaPlacement(placement?: string | null): string {
  switch (placement) {
    case 'FIRST':
      return strings.NOA_PLACEMENT_FIRST
    case 'AFTER_FC_INVOICE':
      return strings.NOA_PLACEMENT_AFTER_FC_INVOICE
    case 'LAST':
      return strings.NOA_PLACEMENT_LAST
    case '':
    case null:
    case undefined:
      return strings.FACTOR_DEFAULT
    default:
      return '-'
  }
}

export function formatDebtorDeliveryMethod(deliveryMethod?: string): string {
  let options = billingDeliveryDropdownOptions
    .filter((option) => option.value === deliveryMethod)
    .map((option) => option.label)
  if (options.length > 0) {
    return options[0]
  }
  return '-'
}

export function displayDisbursementTotalFeeByInvoiceGroupType(params: GridRenderCellParams<any, any, any>) {
  const value =
    params.row.type === 'OVER_ADVANCE' ? currencyFormat(params.row.overAdvanceTotalFees) : currencyFormat(params.value)
  return value
}

export const fuelAdvanceTypeFormat = (fuelAdvanceType?: FuelAdvanceType) => {
  switch (fuelAdvanceType) {
    case FuelAdvanceType.INVOICE:
      return strings.INVOICE
    case FuelAdvanceType.CREDIT:
      return strings.CREDIT
    default:
      return ''
  }
}

export const fuelAdvanceTypeChipColorFormat = (fuelAdvanceType: FuelAdvanceType) => {
  switch (fuelAdvanceType) {
    case FuelAdvanceType.INVOICE:
      return 'info'
    case FuelAdvanceType.CREDIT:
      return 'success'
    default:
      return 'default'
  }
}

export const formatDecimalValue = (value?: number | string | null, decimals: number = 2): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'string' && value.trim() === '') return '-'
  const numeric = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(numeric)) return '-'
  return numeric.toFixed(decimals)
}

export const formatDaysValue = (value?: number | string | null): string => {
  return formatDecimalValue(value, 2)
}

export const formatPartnerType = (partnerType: PartnerType) => {
  switch (partnerType) {
    case PartnerType.REFERRAL_PARTNER:
      return strings.REFERRAL_PARTNER
    default:
      return strings.UNKNOWN
  }
}

export const formatOverAdvanceTypes = (overAdvanceType?: OverAdvanceType | null) =>
  overAdvanceType ? overAdvanceTypeLabel(overAdvanceType) : '-'
